import { Stack, Text } from '@mantine/core';
import { useState } from 'react';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import useStaticDatasetUsage from '@/hooks/api/datasets/static/useStaticDatasetUsage.ts';

export default function StaticDatasetHistory({ datasetId }: { datasetId: string }) {
  const [opened, setOpened] = useState(false);
  const [page, setPage] = useState(1);
  const history = useStaticDatasetUsage(datasetId, true, { enabled: opened, historyPage: page });
  return (
    <Stack gap="xs">
      <StyledButton
        styles={{
          root: {
            backgroundColor: 'var(--mantine-color-gray-0)',
            color: 'var(--mantine-color-gray-7)',
            alignSelf: 'flex-start',
          },
        }}
        variant="subtle"
        size="sm"
        onClick={() => setOpened(!opened)}
        aria-expanded={opened}
      >
        {opened ? 'Hide older-version references' : 'Check older-version references'}
      </StyledButton>
      {opened && (
        <>
          {history.isFetching ? (
            <Text size="sm">Checking older versions…</Text>
          ) : history.isError || history.fetchStatus === 'paused' ? (
            <Text size="sm">
              Older-version references could not be checked. This does not change the current
              removal check.
            </Text>
          ) : (
            history.data && (
              <>
                <Text size="sm">
                  {history.data.results.some((item) =>
                    item.references.some((ref) => ref.kind === 'historical'),
                  )
                    ? 'Earlier saved versions reference this source.'
                    : 'No references found on this page of older versions.'}
                </Text>
                {history.data.history_errors && (
                  <Text size="sm">Some older versions could not be inspected.</Text>
                )}
                {!history.data.history_complete && (
                  <Text size="sm">
                    Historical inspection is incomplete. More versions may reference this source.
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  History page {page}. Historical references do not block removal.
                </Text>
                {history.data.history_next_page && (
                  <StyledButton
                    styles={{
                      root: {
                        backgroundColor: 'var(--mantine-color-gray-0)',
                        color: 'var(--mantine-color-gray-7)',
                        alignSelf: 'flex-start',
                      },
                    }}
                    variant="subtle"
                    size="sm"
                    onClick={() => setPage(history.data!.history_next_page!)}
                  >
                    Next history page
                  </StyledButton>
                )}
              </>
            )
          )}
          {page > 1 && (
            <StyledButton
              styles={{
                root: {
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  color: 'var(--mantine-color-gray-7)',
                  alignSelf: 'flex-start',
                },
              }}
              variant="subtle"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={history.isFetching}
            >
              Previous history page
            </StyledButton>
          )}
          {(history.isError || history.fetchStatus === 'paused') && (
            <StyledButton
              styles={{
                root: {
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  color: 'var(--mantine-color-gray-7)',
                  alignSelf: 'flex-start',
                },
              }}
              variant="subtle"
              size="sm"
              onClick={() => {
                void history.refetch();
              }}
            >
              Retry history check
            </StyledButton>
          )}
        </>
      )}
    </Stack>
  );
}
